import { useStatus } from '../../hooks/useVault';
import { useUi } from '../../store/ui';
import { useSessionContext } from '../../hooks/sessionContext';
import { useQuery } from '@tanstack/react-query';
import { Api } from '../../lib/api';
import { useAgent } from '../../store/agent';

/** Thin, always visible, always honest. Scope §10.2. */
export function StatusBar() {
  const status = useStatus();
  const s = status.data;
  const session = useSessionContext();
  const panes = useUi((st) => st.panes);
  const drafts = useUi((st) => st.drafts);
  const unsaved = panes.filter((p) => p.noteId && drafts[p.noteId]?.dirty).length;
  const w = s?.watcher;
  const providers = useQuery({ queryKey: ['providers'], queryFn: Api.agent.providers, refetchInterval: 60_000, staleTime: 30_000 });
  const agentStatus = useAgent((st) => st.status);
  const agentProvider = useAgent((st) => st.session?.provider);
  const prov = providers.data?.providers.find((p) => p.id === (agentProvider ?? providers.data?.default));
  const agent: { cls: string; text: string; title: string } = !providers.data
    ? { cls: '', text: 'agent …', title: 'checking model providers' }
    : !prov
      ? { cls: 'crit', text: 'no model', title: 'No model provider configured. Set ANTHROPIC_API_KEY or run Ollama.' }
      : !prov.ok
        ? { cls: 'crit', text: `${prov.id} down`, title: prov.detail }
        : agentStatus === 'RUNNING'
          ? { cls: 'teal', text: `${prov.id} · working`, title: prov.model }
          : agentStatus === 'AWAITING_APPROVAL'
            ? { cls: 'warn', text: `${prov.id} · approval`, title: 'A tool call is waiting for your approval' }
            : budgetChip(prov, providers.data.localOnly, providers.data.budget);
  const indexDrift = s ? s.notes !== s.ftsDocs : false;
  const sync: { cls: string; text: string } = !s
    ? { cls: status.isError ? 'crit' : '', text: status.isError ? 'api unreachable' : 'connecting' }
    : s.reindexing
      ? { cls: 'warn', text: 'reindexing' }
      : !w?.running
        ? { cls: 'warn', text: 'watcher off' }
        : w.errors > 0
          ? { cls: 'warn', text: `watcher ${w.errors} err` }
          : w.pending > 0
            ? { cls: 'teal', text: `syncing ${w.pending}` }
            : { cls: 'ok', text: 'synced' };

  return (
    <div className="statusbar">
      <span className="item" title={agent.title}>
        <span className={`light ${agent.cls}`} /> {agent.text}
      </span>
      <span className="sep">│</span>
      <span className="item" title={s ? `${s.notes} notes · ${s.edges} edges · ${s.unresolvedEdges} unresolved · fts ${s.ftsDocs}` : ''}>
        <span className={`light ${indexDrift ? 'warn' : s ? 'ok' : ''}`} />
        {s ? `${s.notes} notes · ${s.edges} links${s.unresolvedEdges ? ` · ${s.unresolvedEdges} unresolved` : ''}${indexDrift ? ` · index drift ${Math.abs(s.notes - s.ftsDocs)}` : ''}` : '…'}
      </span>
      <span className="sep">│</span>
      <span className="item" title={w?.lastEventAt ? `last vault event ${new Date(w.lastEventAt).toLocaleTimeString()}` : 'no vault events yet'}>
        <span className={`light ${sync.cls}`} /> {sync.text}
      </span>
      {s?.embed?.embedder && (
        <>
          <span className="sep">│</span>
          <span className="item" title={`${s.embed.embedder} via ${s.embed.vectorStore}${s.embed.healthy ? '' : ` · ${s.embed.healthDetail}`}`}>
            <span className={`light ${s.embed.pending > 0 ? (s.embed.healthy ? 'teal' : 'warn') : 'ok'}`} />
            {s.embed.pending > 0 ? `embedding ${s.embed.done}/${s.embed.total}${s.embed.ratePerMin ? ` · ${s.embed.ratePerMin}/min` : ''}` : `embeddings ${s.embed.total}`}
          </span>
        </>
      )}
      {s?.git && (
        <>
          <span className="sep">│</span>
          <span className="item">git</span>
        </>
      )}
      <span className="right">
        {unsaved > 0 && <span className="item" style={{ color: 'var(--orange)' }}>● {unsaved} unsaved</span>}
        {session.user && (
          <span className="item" title={session.user.email}>
            {session.user.name} · <span style={{ color: 'var(--teal)' }}>{session.user.role.toLowerCase()}</span>
          </span>
        )}
        <span className="item" title={s?.vaultPath}>{s?.vaultPath?.split(/[\\/]/).slice(-2).join('/') ?? ''}</span>
      </span>
    </div>
  );
}

/** Agent chip text when the provider is healthy and idle: provider, and the month's cloud budget if one is set. */
function budgetChip(prov: { id: string; model: string; latencyMs?: number }, localOnly: boolean, b?: { budgetCents: number; spentCents: number; remainingCents: number | null; exhausted: boolean; month: string }): { cls: string; text: string; title: string } {
  const base = `${prov.model}${prov.latencyMs != null ? ` · ${prov.latencyMs} ms` : ''}`;
  if (!b || b.budgetCents <= 0 || b.remainingCents === null) return { cls: 'ok', text: `${prov.id}${localOnly ? ' · local only' : ''}`, title: base };
  const left = `$${(b.remainingCents / 100).toFixed(2)}`;
  const title = `${base} · cloud spend $${(b.spentCents / 100).toFixed(2)} of $${(b.budgetCents / 100).toFixed(2)} for ${b.month}`;
  if (b.exhausted) return { cls: 'crit', text: `${prov.id} · budget reached`, title: `${title} · cap reached: local model only until next month` };
  const frac = b.remainingCents / b.budgetCents;
  return { cls: frac < 0.2 ? 'warn' : 'ok', text: `${prov.id} · ${left} left`, title };
}
